/* ================================================================
   Editor Store - Zustand-based unified state management.
   Handles: project model, selection, undo/redo, solve pipeline,
   context menu, save/restore.

   Optimizations:
   - Operations layer is pure-immutable (structuredClone), so
     snapshots for undo/redo just store a reference (no clone).
   - Solve pipeline is per-layer memoized (see core/solve).
   - DrawRecords + RenderNodes reuse identity for unchanged layers,
     which lets the viewport skip re-rendering those meshes.
   ================================================================ */

import { create } from 'zustand';
import type {
  ProjectModel, SideId, LayerStyle,
  DrawRecord, RenderNode, EditorSnapshot,
  BomSummary, ValidationReport, ProjectTemplate, SideSpanOverride,
} from '../core/model/types';
import { createDefaultProject } from '../core/model/defaults';
import { solveProject } from '../core/solve';
import { buildDrawRecords, buildRenderNodes } from '../core/recordBuilder';
import {
  changeLayerProps, insertLayer, removeLayer,
  batchChangeLayerProps, removeMultipleLayers,
  changeInnerSize, changeOuterSize, toggleSide,
  changeElementProps,
  type LayerPatch, type ElementPatch,
} from '../core/operations';
import { importLegacyModel } from '../core/adapter';
import { calculateBom } from '../core/bom';
import { validateProject } from '../core/validator';
import {
  applyProjectTemplate, applyStructuralPreset,
  type StructuralPreset,
} from '../core/presets';

// --------------- Types ---------------

export interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
}

export interface EditorState {
  project: ProjectModel;
  drawRecords: DrawRecord[];
  renderNodes: RenderNode[];

  selectedMeshIds: Record<string, boolean>;
  hoveredMeshId: string | null;

  contextMenu: ContextMenuState;

  undoStack: EditorSnapshot[];
  redoStack: EditorSnapshot[];
  maxUndoDepth: number;

  isDirty: boolean;

  /** 当前关联的订单 ID，用于按订单保存/加载 */
  orderId: string | number | null;

  /** UI: which side is focused (for the Ribbon "面" tab). */
  focusedSideId: SideId | null;

  // --------------- Actions ---------------

  newProject: (mode?: 'crate' | 'pallet') => void;
  loadProject: (json: string) => void;
  importLegacy: (json: string) => void;
  applyTemplate: (template: ProjectTemplate) => void;
  applyPreset: (preset: StructuralPreset, sideIds?: SideId[]) => void;

  applyProjectChange: (newProject: ProjectModel) => void;

  changeLayer: (sideId: SideId, layerIndex: number, patch: LayerPatch) => void;
  changeElement: (sideId: SideId, layerIndex: number, elementIndex: number, patch: ElementPatch) => void;
  addLayer: (sideId: SideId, insertIndex: number, style?: LayerStyle, direction?: 0 | 1) => void;
  deleteLayer: (sideId: SideId, layerIndex: number) => void;
  batchChangeLayer: (targets: Array<{ sideId: SideId; layerIndex: number }>, patch: LayerPatch) => void;
  toggleSideEnabled: (sideId: SideId, enabled: boolean) => void;

  setInnerSize: (size: Partial<{ length: number; width: number; height: number }>) => void;
  setOuterSize: (size: Partial<{ length: number; width: number; height: number }>) => void;

  selectMesh: (meshId: string, additive?: boolean) => void;
  deselectAll: () => void;
  setHoveredMesh: (meshId: string | null) => void;
  deleteSelected: () => void;

  setFocusedSide: (sideId: SideId | null) => void;
  setSideLinkageMode: (mode: 'linked' | 'independent') => void;

  showContextMenu: (x: number, y: number) => void;
  hideContextMenu: () => void;

  undo: () => void;
  redo: () => void;

  setOrderId: (id: string | number | null) => void;
  saveToLocal: () => void;
  loadFromLocal: (orderId?: string | number) => boolean;

  reSolve: () => void;
}

// --------------- Storage key ---------------

const STORAGE_KEY_PREFIX = 'pack3d-designer-project';
function storageKey(orderId?: string | number): string {
  return orderId ? `${STORAGE_KEY_PREFIX}-${orderId}` : STORAGE_KEY_PREFIX;
}

// --------------- Pipeline helper ---------------

interface PipelineOutput {
  drawRecords: DrawRecord[];
  renderNodes: RenderNode[];
}

const DEFAULT_LAYER_COLOR = '#c8a96e';
const LEGACY_DEFAULT_MATERIAL_KEY = 1;
const RADIATA_PINE_MATERIAL_KEY = 4;

function normalizeDefaultMaterial(project: ProjectModel): ProjectModel {
  let changed = false;
  const nextProject: ProjectModel = structuredClone
    ? structuredClone(project)
    : JSON.parse(JSON.stringify(project));

  for (const side of nextProject.sides) {
    for (const layer of side.layers) {
      const usesLegacyDefaultMaterial =
        layer.materialKey === LEGACY_DEFAULT_MATERIAL_KEY
        && layer.styleSetting.material === LEGACY_DEFAULT_MATERIAL_KEY;
      const usesDefaultColor =
        layer.color === DEFAULT_LAYER_COLOR
        && (layer.styleSetting.materialcolor === '' || layer.styleSetting.materialcolor === DEFAULT_LAYER_COLOR);

      if (usesLegacyDefaultMaterial && usesDefaultColor) {
        layer.materialKey = RADIATA_PINE_MATERIAL_KEY;
        layer.styleSetting.material = RADIATA_PINE_MATERIAL_KEY;
        changed = true;
      }
    }
  }

  return changed ? nextProject : project;
}

function computeFrozenSideSpanOverrides(project: ProjectModel): Partial<Record<SideId, SideSpanOverride>> {
  const totals: Record<SideId, number> = {
    pedestal: 0, topCap: 0, sideFront: 0, sideBack: 0, endLeft: 0, endRight: 0,
  };

  for (const side of project.sides) {
    if (!side.enabled) continue;
    totals[side.sideId] = side.layers.reduce((sum, layer) => sum + layer.thickness, 0);
  }

  const sideHeight = Math.max(0, project.outerSize.height - totals.pedestal - totals.topCap);
  const currentMode = project.sideLinkageMode ?? 'linked';
  const existing = project.sideSpanOverrides ?? {};
  const pedestalSpanU = currentMode === 'linked' ? project.outerSize.length : (existing.pedestal?.spanU ?? project.outerSize.length);
  const pedestalSpanV = currentMode === 'linked' ? project.outerSize.width : (existing.pedestal?.spanV ?? project.outerSize.width);
  const pedestalOffsetU = currentMode === 'linked' ? 0 : (existing.pedestal?.offsetU ?? 0);
  const pedestalOffsetV = currentMode === 'linked' ? 0 : (existing.pedestal?.offsetV ?? 0);
  const frontBackSpanU = currentMode === 'linked' ? project.outerSize.length : (existing.sideFront?.spanU ?? project.innerSize.length);
  const endSpanU = currentMode === 'linked' ? project.outerSize.width : (existing.endLeft?.spanU ?? project.innerSize.width);
  const frontBackOffset = currentMode === 'linked' ? 0 : (existing.sideFront?.offsetU ?? 0);
  const endOffset = currentMode === 'linked' ? 0 : (existing.endLeft?.offsetU ?? 0);

  return {
    pedestal: {
      spanU: pedestalSpanU,
      spanV: pedestalSpanV,
      offsetU: pedestalOffsetU,
      offsetV: pedestalOffsetV,
    },
    topCap: {
      spanU: pedestalSpanU,
      spanV: pedestalSpanV,
      offsetU: pedestalOffsetU,
      offsetV: pedestalOffsetV,
    },
    sideFront: { spanU: frontBackSpanU, spanV: sideHeight, offsetU: frontBackOffset, offsetV: 0 },
    sideBack: { spanU: frontBackSpanU, spanV: sideHeight, offsetU: frontBackOffset, offsetV: 0 },
    endLeft: { spanU: endSpanU, spanV: sideHeight, offsetU: endOffset, offsetV: 0 },
    endRight: { spanU: endSpanU, spanV: sideHeight, offsetU: endOffset, offsetV: 0 },
  };
}

function normalizeSideLinkage(project: ProjectModel): ProjectModel {
  if (project.sideLinkageMode === 'independent' && !project.sideSpanOverrides) {
    return {
      ...project,
      sideSpanOverrides: computeFrozenSideSpanOverrides(project),
    };
  }
  return project;
}

function runPipeline(project: ProjectModel): PipelineOutput {
  const solvedLayouts = solveProject(project);
  const drawRecords = buildDrawRecords(project, solvedLayouts);
  const renderNodes = buildRenderNodes(drawRecords);
  return { drawRecords, renderNodes };
}

// --------------- Store ---------------

export const useEditorStore = create<EditorState>((set, get) => {
  const defaultProject = createDefaultProject('crate');
  const initial = runPipeline(defaultProject);

  return {
    project: defaultProject,
    drawRecords: initial.drawRecords,
    renderNodes: initial.renderNodes,
    selectedMeshIds: {},
    hoveredMeshId: null,
    contextMenu: { visible: false, x: 0, y: 0 },
    undoStack: [],
    redoStack: [],
    maxUndoDepth: 50,
    isDirty: false,
    orderId: null,
    focusedSideId: null,

    // ---- Project init ----

    newProject(mode = 'crate') {
      const project = createDefaultProject(mode);
      const pipeline = runPipeline(project);
      set({
        project,
        ...pipeline,
        selectedMeshIds: {},
        hoveredMeshId: null,
        undoStack: [],
        redoStack: [],
        isDirty: false,
        contextMenu: { visible: false, x: 0, y: 0 },
        focusedSideId: null,
      });
    },

    loadProject(json: string) {
      try {
        const project = normalizeSideLinkage(normalizeDefaultMaterial(JSON.parse(json) as ProjectModel));
        const pipeline = runPipeline(project);
        set({
          project,
          ...pipeline,
          selectedMeshIds: {},
          undoStack: [],
          redoStack: [],
          isDirty: false,
        });
      } catch (e) {
        console.error('Failed to load project:', e);
      }
    },

    importLegacy(json: string) {
      try {
        const project = normalizeSideLinkage(normalizeDefaultMaterial(importLegacyModel(json)));
        const pipeline = runPipeline(project);
        set({
          project,
          ...pipeline,
          selectedMeshIds: {},
          undoStack: [],
          redoStack: [],
          isDirty: false,
        });
      } catch (e) {
        console.error('Failed to import legacy model:', e);
      }
    },

    applyTemplate(template) {
      const project = applyProjectTemplate(template);
      const pipeline = runPipeline(project);
      set({
        project,
        ...pipeline,
        selectedMeshIds: {},
        undoStack: [],
        redoStack: [],
        isDirty: true,
        focusedSideId: null,
      });
    },

    applyPreset(preset, sideIds) {
      const state = get();
      const newProject = applyStructuralPreset(state.project, preset, sideIds);
      state.applyProjectChange(newProject);
    },

    // ---- Core pipeline ----

    applyProjectChange(newProject: ProjectModel) {
      const state = get();
      // Previous project ref is already immutable from external view
      // (operations always produce new refs), so snapshot can reuse it.
      const snapshot: EditorSnapshot = {
        project: state.project,
        timestamp: Date.now(),
      };

      const undoStack = state.undoStack.length >= state.maxUndoDepth
        ? [...state.undoStack.slice(-(state.maxUndoDepth - 1)), snapshot]
        : [...state.undoStack, snapshot];

      const pipeline = runPipeline(newProject);

      set({
        project: newProject,
        ...pipeline,
        undoStack,
        redoStack: [],
        isDirty: true,
      });
    },

    // ---- Layer operations ----

    changeLayer(sideId, layerIndex, patch) {
      const state = get();
      const newProject = changeLayerProps(state.project, sideId, layerIndex, patch);
      state.applyProjectChange(newProject);
    },

    changeElement(sideId, layerIndex, elementIndex, patch) {
      const state = get();
      const newProject = changeElementProps(state.project, sideId, layerIndex, elementIndex, patch);
      state.applyProjectChange(newProject);
    },

    addLayer(sideId, insertIndex, style = 0, direction = 0) {
      const state = get();
      const newProject = insertLayer(state.project, sideId, insertIndex, style, direction);
      state.applyProjectChange(newProject);
    },

    deleteLayer(sideId, layerIndex) {
      const state = get();
      const newProject = removeLayer(state.project, sideId, layerIndex);
      state.applyProjectChange(newProject);
    },

    batchChangeLayer(targets, patch) {
      const state = get();
      const newProject = batchChangeLayerProps(state.project, targets, patch);
      state.applyProjectChange(newProject);
    },

    toggleSideEnabled(sideId, enabled) {
      const state = get();
      const newProject = toggleSide(state.project, sideId, enabled);
      state.applyProjectChange(newProject);
    },

    // ---- Size operations ----

    setInnerSize(size) {
      const state = get();
      const newProject = changeInnerSize(state.project, size);
      state.applyProjectChange(newProject);
    },

    setOuterSize(size) {
      const state = get();
      const newProject = changeOuterSize(state.project, size);
      state.applyProjectChange(newProject);
    },

    // ---- Selection ----

    selectMesh(meshId, additive = false) {
      set(state => {
        const prev = additive ? { ...state.selectedMeshIds } : {};
        if (prev[meshId]) {
          delete prev[meshId];
        } else {
          prev[meshId] = true;
        }
        return { selectedMeshIds: prev, contextMenu: { visible: false, x: 0, y: 0 } };
      });
    },

    deselectAll() {
      set({
        selectedMeshIds: {},
        contextMenu: { visible: false, x: 0, y: 0 },
      });
    },

    setHoveredMesh(meshId) {
      const prev = get().hoveredMeshId;
      if (prev === meshId) return;
      set({ hoveredMeshId: meshId });
    },

    deleteSelected() {
      const state = get();
      if (Object.keys(state.selectedMeshIds).length === 0) return;

      const targets = new Map<string, { sideId: SideId; layerIndex: number }>();
      for (const node of state.renderNodes) {
        if (state.selectedMeshIds[node.meshId]) {
          const key = `${node.sideId}-${node.layerIndex}`;
          if (!targets.has(key)) {
            targets.set(key, { sideId: node.sideId, layerIndex: node.layerIndex });
          }
        }
      }

      if (targets.size > 0) {
        const newProject = removeMultipleLayers(state.project, [...targets.values()]);
        state.applyProjectChange(newProject);
        set({ selectedMeshIds: {} });
      }
    },

    // ---- Side focus ----

    setFocusedSide(sideId) {
      set({ focusedSideId: sideId });
    },

    setSideLinkageMode(mode) {
      const state = get();
      if ((state.project.sideLinkageMode ?? 'linked') === mode) return;
      const sideSpanOverrides = mode === 'independent'
        ? computeFrozenSideSpanOverrides(state.project)
        : undefined;
      state.applyProjectChange({
        ...state.project,
        sideLinkageMode: mode,
        sideSpanOverrides,
        meta: { ...state.project.meta, updatedAt: new Date().toISOString() },
      });
    },

    // ---- Context menu ----

    showContextMenu(x, y) {
      set({ contextMenu: { visible: true, x, y } });
    },

    hideContextMenu() {
      set({ contextMenu: { visible: false, x: 0, y: 0 } });
    },

    // ---- Undo/redo ----

    undo() {
      const state = get();
      if (state.undoStack.length === 0) return;

      const prev = state.undoStack[state.undoStack.length - 1];
      const currentSnapshot: EditorSnapshot = {
        project: state.project,
        timestamp: Date.now(),
      };

      const pipeline = runPipeline(prev.project);
      set({
        project: prev.project,
        ...pipeline,
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, currentSnapshot],
        isDirty: true,
      });
    },

    redo() {
      const state = get();
      if (state.redoStack.length === 0) return;

      const next = state.redoStack[state.redoStack.length - 1];
      const currentSnapshot: EditorSnapshot = {
        project: state.project,
        timestamp: Date.now(),
      };

      const pipeline = runPipeline(next.project);
      set({
        project: next.project,
        ...pipeline,
        undoStack: [...state.undoStack, currentSnapshot],
        redoStack: state.redoStack.slice(0, -1),
        isDirty: true,
      });
    },

    // ---- Save/load ----

    setOrderId(id) {
      set({ orderId: id });
    },

    saveToLocal() {
      const state = get();
      const key = storageKey(state.orderId ?? undefined);
      try {
        const json = JSON.stringify(state.project);
        localStorage.setItem(key, json);
        set({ isDirty: false });
      } catch (e) {
        console.error('Save failed:', e);
      }
    },

    loadFromLocal(orderId?) {
      try {
        const key = storageKey(orderId ?? get().orderId ?? undefined);
        const json = localStorage.getItem(key);
        if (!json) return false;
        const project = normalizeSideLinkage(normalizeDefaultMaterial(JSON.parse(json) as ProjectModel));
        const pipeline = runPipeline(project);
        set({
          project,
          ...pipeline,
          selectedMeshIds: {},
          undoStack: [],
          redoStack: [],
          isDirty: false,
        });
        return true;
      } catch (e) {
        console.error('Load failed:', e);
        return false;
      }
    },

    // ---- Re-solve ----

    reSolve() {
      const state = get();
      const pipeline = runPipeline(state.project);
      set(pipeline);
    },
  };
});

// --------------- Derived selectors ---------------

export function getSelectedNodes(state: EditorState): RenderNode[] {
  const out: RenderNode[] = [];
  for (const n of state.renderNodes) {
    if (state.selectedMeshIds[n.meshId]) out.push(n);
  }
  return out;
}

export function getSelectedLayerInfo(state: EditorState) {
  const nodes = getSelectedNodes(state);
  if (nodes.length === 0) return null;

  const first = nodes[0];
  const side = state.project.sides[first.sideIndex];
  const layer = side?.layers[first.layerIndex];
  return { sideId: first.sideId, sideIndex: first.sideIndex, layerIndex: first.layerIndex, layer, side };
}

export function getSelectedLayers(state: EditorState) {
  const seen = new Set<string>();
  const out: Array<{ sideId: SideId; layerIndex: number }> = [];
  for (const n of state.renderNodes) {
    if (!state.selectedMeshIds[n.meshId]) continue;
    const key = `${n.sideId}-${n.layerIndex}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ sideId: n.sideId, layerIndex: n.layerIndex });
  }
  return out;
}

export function getProjectStats(state: EditorState) {
  const p = state.project;
  let layerCount = 0;
  let enabledSides = 0;
  for (const s of p.sides) {
    if (s.enabled) {
      enabledSides++;
      layerCount += s.layers.length;
    }
  }
  return {
    meshCount: state.renderNodes.length,
    layerCount,
    enabledSides,
    mode: p.meta.mode,
  };
}

// --------------- Memoized derived views (BOM / Validation) ---------------
// Both are derived from (project, renderNodes). We memoize by reference
// equality so repeated reads inside one render pass are free.

interface DerivedCache {
  projectRef: ProjectModel | null;
  nodesRef: RenderNode[] | null;
  bom: BomSummary | null;
  validation: ValidationReport | null;
}

const derivedCache: DerivedCache = {
  projectRef: null,
  nodesRef: null,
  bom: null,
  validation: null,
};

function refreshDerived(state: EditorState): void {
  if (derivedCache.projectRef !== state.project || derivedCache.nodesRef !== state.renderNodes) {
    derivedCache.projectRef = state.project;
    derivedCache.nodesRef = state.renderNodes;
    derivedCache.bom = calculateBom(state.project, state.renderNodes);
    derivedCache.validation = validateProject(state.project, derivedCache.bom);
  }
}

export function getBomSummary(state: EditorState): BomSummary {
  refreshDerived(state);
  return derivedCache.bom!;
}

export function getValidationReport(state: EditorState): ValidationReport {
  refreshDerived(state);
  return derivedCache.validation!;
}
